import React, { ReactNode, useEffect, useState } from "react"
import { Button, Col, Row } from "react-bootstrap"
import { FormattedMessage, injectIntl } from "react-intl"
import { useMutation, useQuery } from "react-query"
import EventBus from "../../common/EventBus"
import { Header } from "../../common/headers"
import Icon from "../../common/icon/icon.component"
import SideBar from "../../common/layout/sidebar.component"
import StandardList from "../../common/listing/list.component"
import NoResult from "../../common/listing/noresult.component"
import Pagination from "../../common/listing/pagination.component"
import SortLink from "../../common/listing/sortlink.component"
import Search from "../../common/search-filter/search/search.component"
import LoadingSpinner from "../../common/spinner/loading.component"
import { SearchPreference } from "../../payment/listing/types"
import IntlProps from "../../state/helpers/IntlProps"
import { OBJECT_TYPE, Service } from "../../state/inventory/imports/service"
import { HEADER_KEYS } from "../../state/inventory/imports/service/types"
import ListingHelper from "../../state/listing/ListingHelper"
import { PREFERENCE_TYPE } from "../../state/listing/constants"
import { STOCK_IMPORTS_LISTING_SECTION_MESSAGES, STOCK_IMPORT_PAGE_MESSAGES } from "../../utils/inventory"
import { HEADER_KEYS_FIELDS, mapStockImportsToRows } from "../../utils/inventory/utils"
import { MainTabsComponent } from "../components"
import StockNewImportModalComponent from "../components/StockNewImportModalComponent"

export const StockImportsPage = injectIntl((props: IntlProps) => {
    const [modal, setModal] = useState<ReactNode>(null)
    const [paginatedSearch, setPaginatedSearch] = useState<SearchPreference>({
        activePage: 1,
        pageSize: 25,
        sortByField: "createdAt",
    } as SearchPreference)

    const handleModal = () => {
        const modal = [] as JSX.Element[]
        modal.push(<StockNewImportModalComponent
            onImportStartedCallback={handleImportStarted}
        />)
        setModal(modal)
    }

    const handleImportStarted = (importStarted:boolean) => {
        setModal(null)
        if (importStarted) {
            window.location.reload()
            EventBus.dispatch(EventBus.TopicSendSuccessNotification, {
                message: (
                    <FormattedMessage
                        id="inventory.components.stock_imports_modal.import_in_progress"
                        defaultMessage="The import operation started.
                        Refresh the page to see the progress of your import."
                    />
                )
            })
        }
    }

    useEffect(() => {
        SideBar.activateMenu("inventory")
    }, [])

    const { isFetchedAfterMount: isPreferenceFetched } = useQuery(
        ["StockImportListingService.getListingPreferences"], () => {
            return  ListingHelper.getListingPreferences(PREFERENCE_TYPE.LISTING_STOCK_IMPORTS)
        }, 
        {
            onSuccess: (preferences) => {
                updatePaginatedSearch({
                    ...preferences,
                    activePage: paginatedSearch.activePage,
                    sortByField: preferences.sortByField || paginatedSearch.sortByField,
                }, false)
            }
        })

    
    const queryKey = ["stock-import-listing", paginatedSearch]
    const { data, isLoading, isFetched } = useQuery(queryKey, () => {
        return Service.fetch({
            ...paginatedSearch,
        })
    },
    {
        enabled: isPreferenceFetched,
    })

    const totalItemsCount = data?.metadata?.totalItemsCount || 0
    const { mutate: savePreferences } = useMutation((paginatedSearch: SearchPreference) => {
        return ListingHelper.saveListingPreferences(PREFERENCE_TYPE.LISTING_STOCK_IMPORTS, paginatedSearch)
    })

    const updatePaginatedSearch = ((newState: Partial<SearchPreference>, shouldSavePreferences = true) => {
        setPaginatedSearch((curr) => ({ ...curr, ...newState }))
        shouldSavePreferences && savePreferences({ ...paginatedSearch, ...newState } as SearchPreference)
    })


    const handlePaginationChange = (activePage: number) => {
        updatePaginatedSearch({ activePage })
    }

    const handlePageSizeChange = (pageSize: number) => {
        const updatedPaginatedSearch: Partial<SearchPreference> = { pageSize }

        if (pageSize * paginatedSearch.activePage >totalItemsCount ) {
            updatedPaginatedSearch.activePage = 1
        }

        updatePaginatedSearch(updatedPaginatedSearch)
    }

  

    const refreshSearch = (savePrefs, refreshTotal, refreshStatBox, data, filters, keywords, savedFilterId) => {   
        updatePaginatedSearch({
            activePage: 1,
            filters,
            keywords,
            savedFilterId,
        }, isPreferenceFetched)
    }

    const handleSortLinkChange = (sortByField: string, sortAsc: boolean) => {
        updatePaginatedSearch({
            sortByField,
            sortAsc,
        })
    } 

    const getHeadersData = (
        sortByField: string,
        sortAsc: boolean,
        handleSortLinkChange:(sortByField: string,
            sortAsc: boolean)=>void,
    ) => {
        return HEADER_KEYS.map(key => {
            return <SortLink
                key={key}
                field={HEADER_KEYS_FIELDS[key]}
                sortField={sortByField}
                asc={sortAsc}
                onChange={handleSortLinkChange}
            >
                {props.intl.formatMessage(STOCK_IMPORTS_LISTING_SECTION_MESSAGES[`${key}`])}
            </SortLink>
        })
    }

    const headers = {
        data: getHeadersData(paginatedSearch.sortByField, paginatedSearch.sortAsc, handleSortLinkChange),
        classNames: [],
    }

    const rows = data?.data.map(i => mapStockImportsToRows(i))

    return (
        <>
            <MainTabsComponent/>
            <Header title={props.intl.formatMessage(STOCK_IMPORT_PAGE_MESSAGES["header"])}>
            </Header>
            <hr/>
            <div className="pb-2">
                {isPreferenceFetched ? <Search
                    onChange={refreshSearch}
                    key={OBJECT_TYPE}
                    typeOfSearch={OBJECT_TYPE}
                    object={[OBJECT_TYPE]}
                    skipInitialChangeEvent={true}
                    searchKeywords={paginatedSearch.keywords}
                    filtersToUse={paginatedSearch.filters}
                    savedFilterId={paginatedSearch.savedFilterId}
                    searchBarClass={"col-md-6"}
                    childrenClass={"col-md-2"}
                    placeholder={props.intl.formatMessage(STOCK_IMPORTS_LISTING_SECTION_MESSAGES["searchPlaceHolder"])}
                >
                    <Button className="btn btn-primary" onClick={handleModal}>
                        <Icon value="download" className="pr-1" />
                        {props.intl.formatMessage(STOCK_IMPORT_PAGE_MESSAGES["importStockButton"])}
                    </Button>
                </Search> : <></>
                }
            </div>
            <>
                {isLoading || !isFetched || !isPreferenceFetched?
                    <LoadingSpinner />
                    : <>
                        <StandardList
                            className={"row pt-3"}
                            headers={headers}
                            rows={rows}
                            pageSize={paginatedSearch.pageSize}
                            smallLine={true}
                            totalItemsCount={totalItemsCount}
                            showSelectionSize={false}
                            footer={
                                totalItemsCount == 0 || rows?.length == 0 ? null :
                                    <Row className={"d-flex justify-content-end m-0"}>
                                        <Col xs={2}>
                                            {totalItemsCount}
                                        </Col>
                                    </Row> }
                            footerClassName={"bg-secondary text-white"}
                        />
                        {rows?.length == 0 && <NoResult className={"pt-0"} noButton={true} />}
                        <Pagination
                            className={"px-2"}
                            activePage={paginatedSearch.activePage}
                            pageSize={paginatedSearch.pageSize}
                            showPageSize={true}
                            totalItemsCount={totalItemsCount}
                            onChange={handlePaginationChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    </>}
                {modal}
            </>
        </>
    )
})


// WEBPACK FOOTER //
// ./src/inventory/pages/StockImportsPage.tsx